// The Hiring Manager's Candidate decision card (DC-223, G §8): under the Hiring Manager's own scorecard, *Pending* until
// the decision is recorded, then Advance (the one primary) · Hold · Pass. Before the scorecard is submitted the card's
// buttons are blocked (DC-24, dashed) with his sentence: "HM interviews, submits scorecard and decides advance/hold/pass."
// The decision is V1 `candidate.decide_advance`; the server keeps its two-2s and touchpoint-cap refusals.

import { useState } from "react";
import { candidateDecideAdvance } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { ACTION_LABELS, Button, CANDIDATE_DECISION_STATUS, Card, Flex, Pill, Recorded, type Blocked, type Tone } from "@/mc";
import { SCORECARD_PAGE_LABELS } from "./labels";

export type CandidateDecision = "advance" | "hold" | "pass";

const DECISION_TONE: Readonly<Record<CandidateDecision | "pending", Tone>> = { pending: "neutral", advance: "ok", hold: "warn", pass: "neutral" };

export interface CandidateDecisionCardProps {
  requisitionId: string;
  applicationId: string;
  /** The Hiring Manager's own scorecard is submitted: the decision opens. */
  submitted: boolean;
  /** A decision already on the application. */
  decided?: CandidateDecision | null | undefined;
  /** The seat may decide (the manifest holds the tool). */
  can?: boolean | undefined;
  /** The reason the Hiring Manager gives with the decision (the scorecard's rationale). */
  reasonText: string;
}

export function CandidateDecisionCard({ requisitionId, applicationId, submitted, decided = null, can, reasonText }: CandidateDecisionCardProps) {
  const write = useWrite(candidateDecideAdvance, [["packets"], ["scorecard"], ["applications"]]);
  const [chosen, setChosen] = useState<CandidateDecision | null>(decided);
  const [busy, setBusy] = useState<CandidateDecision | null>(null);
  if (can === false) return null;
  const blocked: Blocked | undefined = !submitted ? { reason: SCORECARD_PAGE_LABELS.hmDecidesAfterScorecard } : undefined;
  const decide = async (value: CandidateDecision) => {
    setBusy(value);
    try {
      await write.mutateAsync({ requisition_id: requisitionId, application_id: applicationId, value, reason_text: reasonText });
      setChosen(value);
    } finally {
      setBusy(null);
    }
  };
  const status = chosen ?? "pending";
  const button = (value: CandidateDecision, tone: "primary" | undefined) => (
    <Button
      key={value}
      tone={tone}
      sm
      blocked={blocked}
      busy={busy === value}
      
      onClick={() => { void decide(value); }}
    >
      {ACTION_LABELS[value]}
    </Button>
  );
  return (
    <Card
      title={SCORECARD_PAGE_LABELS.candidateDecision}
      pills={<Pill tone={DECISION_TONE[status]}>{CANDIDATE_DECISION_STATUS[status]}</Pill>}
      sub={submitted ? undefined : SCORECARD_PAGE_LABELS.hmDecidesAfterScorecard}
    >
      {chosen
        ? <Recorded live>{CANDIDATE_DECISION_STATUS[chosen]}</Recorded>
        : <Flex gap={8}>{button("advance", "primary")}{button("hold", undefined)}{button("pass", undefined)}</Flex>}
    </Card>
  );
}
